'use client'

import { useEffect, useRef, RefObject } from 'react'

interface ScrollAnimationOptions {
  threshold?: number
  rootMargin?: string
}

/**
 * Hook for scroll-triggered animations using IntersectionObserver
 * Adds 'visible' class to [data-scroll-item] elements when they enter viewport
 */
export function useScrollAnimation<T extends HTMLElement>(
  options: ScrollAnimationOptions = {}
): RefObject<T | null> {
  const { threshold = 0.15, rootMargin = '0px 0px -50px 0px' } = options
  const sectionRef = useRef<T | null>(null)

  useEffect(() => {
    const section = sectionRef.current
    if (!section) return

    const items = section.querySelectorAll('[data-scroll-item]')

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold, rootMargin }
    )

    items.forEach((item) => observer.observe(item))

    return () => observer.disconnect()
  }, [threshold, rootMargin])

  return sectionRef
}
